import { API_VERSION } from './constants';

const authRoot = `${API_VERSION.V1}/auth`;

const AUTH = {
  login: `${authRoot}/login`,
  ssoLogin: `${authRoot}/sso-login`,
  refresh: `${authRoot}/refresh`,
  logout: `${authRoot}/logout`,
  profile: `${authRoot}/profile`,
  register: `${authRoot}/register`,
  changePassword: `${authRoot}/change-password`,
  permissions: `${authRoot}/permissions`,
} as const;

const usersRoot = `${API_VERSION.V1}/users`;

const USERS = {
  list: usersRoot,
  create: usersRoot,
  detail: (id: string) => `${usersRoot}/${id}`,
  update: (id: string) => `${usersRoot}/${id}`,
  delete: (id: string) => `${usersRoot}/${id}`,
  resetPassword: (id: string) => `${usersRoot}/${id}/change-password`,
  reportingAuthorities: `${usersRoot}/reporting-authorities`,
} as const;

const attendanceRoot = `${API_VERSION.V1}/attendance`;

const ATTENDANCE = {
  dailyDashboard: `${attendanceRoot}/daily-dashboard`,
  dailyDashboardExport: `${attendanceRoot}/daily-dashboard/export`,
  jobCard: `${attendanceRoot}/job-card`,
  jobCardExport: `${attendanceRoot}/job-card/export`,
  shiftJobCard: `${attendanceRoot}/shift-job-card`,
  shiftJobCardExport: `${attendanceRoot}/shift-job-card/export`,
  employeeReport: `${attendanceRoot}/employee-report`,
  shiftEmployeeReport: `${attendanceRoot}/shift-employee-report`,
} as const;

const ORGANIZATION = {
  companies: `${API_VERSION.V1}/companies`,
  departments: `${API_VERSION.V1}/departments`,
  designations: `${API_VERSION.V1}/designations`,
} as const;

const geoUnitsRoot = `${API_VERSION.V1}/geo-units`;

const GEO = {
  units: geoUnitsRoot,
  divisions: `${geoUnitsRoot}/divisions`,
  districts: `${geoUnitsRoot}/districts`,
  thanas: `${geoUnitsRoot}/thanas`,
} as const;

const SETTINGS = {
  userTypes: `${API_VERSION.V1}/settings/user-types`,
} as const;

const leaveTypesRoot = `${API_VERSION.V1}/settings/leave-types`;

const LEAVE_TYPE = {
  list: leaveTypesRoot,
  create: leaveTypesRoot,
  update: (id: number) => `${leaveTypesRoot}/${id}`,
  delete: (id: number) => `${leaveTypesRoot}/${id}`,
} as const;

const holidaysRoot = `${API_VERSION.V1}/settings/holidays`;

const HOLIDAY = {
  list: holidaysRoot,
  create: holidaysRoot,
  update: (id: number) => `${holidaysRoot}/${id}`,
  delete: (id: number) => `${holidaysRoot}/${id}`,
} as const;

const limitTypesRoot = `${API_VERSION.V1}/settings/limit-types`;

const LIMIT_TYPE = {
  list: limitTypesRoot,
  update: (id: number) => `${limitTypesRoot}/${id}`,
} as const;

const webUrlsRoot = `${API_VERSION.V1}/settings/web-urls`;

const WEB_URL = {
  list: webUrlsRoot,
  create: webUrlsRoot,
  update: (id: number) => `${webUrlsRoot}/${id}`,
  delete: (id: number) => `${webUrlsRoot}/${id}`,
} as const;

const APP_VERSION = {
  list: `${API_VERSION.V1}/settings/app-versions`,
} as const;

const APP_VERSION_CHECK = {
  report: `${API_VERSION.V1}/reports/app-version-check`,
} as const;

const dcrSummaryRoot = `${API_VERSION.V1}/reports/dcr-summary`;

const DCR_SUMMARY = {
  list: dcrSummaryRoot,
  export: `${dcrSummaryRoot}/export`,
} as const;

const dashboardsRoot = `${API_VERSION.V1}/reports/dashboards`;

const DASHBOARD = {
  system: `${dashboardsRoot}/system`,
  landing: `${dashboardsRoot}/landing`,
  systemExport: `${dashboardsRoot}/system/export`,
  dcr: `${dashboardsRoot}/dcr`,
  der: `${dashboardsRoot}/der`,
  derExport: `${dashboardsRoot}/der/export`,
  workSummary: (userId: number) => `${dashboardsRoot}/members/${userId}/work-summary`,
  pendingSummary: `${dashboardsRoot}/pending-summary`,
} as const;

const SETTINGS_LOOKUPS = {
  fiscalYears: `${API_VERSION.V1}/settings/fiscal-years`,
  menuLabels: `${API_VERSION.V1}/settings/menu-labels`,
} as const;

const billingRoot = `${API_VERSION.V1}/billing`;

const BILLING = {
  transportTypes: `${billingRoot}/transport-types`,
  dcrEntry: (recordId: number) => `${billingRoot}/dcr-entries/${recordId}`,
  offlineDcrVerify: `${billingRoot}/offline-dcr-verify`,
  verifyDcr: (recordId: number) => `${billingRoot}/offline-dcr-verify/${recordId}`,
  offlineDerVerify: `${billingRoot}/offline-der-verify`,
  verifyDer: (detailId: number) => `${billingRoot}/offline-der-verify/${detailId}`,
  derAttachment: (detailId: number) => `${billingRoot}/offline-der-verify/${detailId}/attachment`,
  expenseTypes: `${billingRoot}/expense-types`,
  dcrApproveSearch: `${billingRoot}/dcr-approve`,
  dcrApproval: `${billingRoot}/dcr-approval`,
  approveDcr: (recordId: number) => `${billingRoot}/dcr-approval/${recordId}`,
  derApproval: `${billingRoot}/der-approval`,
  approveDer: (detailId: number) => `${billingRoot}/der-approval/${detailId}`,
  expenseApproval: `${billingRoot}/expense-approval`,
  expenseApprovalDetail: (expenseId: number) => `${billingRoot}/expense-approval/${expenseId}`,
  approveExpense: (expenseId: number) => `${billingRoot}/expense-approval/${expenseId}`,
  allCost: `${billingRoot}/all-cost`,
  amendDcr: (recordId: number) => `${billingRoot}/cost-amendment/dcr/${recordId}`,
  amendDer: (detailId: number) => `${billingRoot}/cost-amendment/der/${detailId}`,
  webExpenses: `${billingRoot}/web-expenses`,
} as const;

const productRoot = `${API_VERSION.V1}/product`;

const PRODUCT = {
  specimenDistribution: `${productRoot}/specimen-distribution`,
  distributionMembers: (specimenId: number) =>
    `${productRoot}/specimen-distribution/${specimenId}/members`,
  distributionInstitutes: (specimenId: number, userId: number) =>
    `${productRoot}/specimen-distribution/${specimenId}/members/${userId}/institutes`,
  specimenDistributionExport: `${productRoot}/specimen-distribution/export`,
  specimenDistributionAll: `${productRoot}/specimen-distribution/all`,
  specimenInstitution: `${productRoot}/specimen-institution`,
  specimenInstitutionExport: `${productRoot}/specimen-institution/export`,
  entryTeacherName: (entryId: number) =>
    `${productRoot}/specimen-institution/entries/${entryId}/teacher-name`,
  specimensExport: `${productRoot}/specimens/export`,
  dcrSheet: `${productRoot}/dcr-sheet`,
  dcrSheetInstitutes: `${productRoot}/dcr-sheet/institutes`,
  bookTypes: `${productRoot}/book-types`,
  bookType: (id: number) => `${productRoot}/book-types/${id}`,
  bookEditions: `${productRoot}/book-editions`,
  bookEdition: (id: number) => `${productRoot}/book-editions/${id}`,
  specimens: `${productRoot}/specimens`,
  specimen: (id: number) => `${productRoot}/specimens/${id}`,

  specimenRequisitions: `${productRoot}/specimen-requisitions`,
  specimenRequisitionSheet: `${productRoot}/specimen-requisitions/sheet`,
  specimenRequisitionDecision: (id: number) =>
    `${productRoot}/specimen-requisitions/${id}/decision`,

  spPromoRequisitionReport: `${productRoot}/sp-promo-requisitions/report`,
  spPromoRequisitionMembers: `${productRoot}/sp-promo-requisitions/members`,

  studentProjects: `${productRoot}/student-projects`,
  studentProject: (id: number) => `${productRoot}/student-projects/${id}`,
  studentClassCategories: (instituteTypeId: number) =>
    `${productRoot}/student-projects/class-categories/${instituteTypeId}`,
  studentEntrySheet: (id: number) => `${productRoot}/student-projects/${id}/entry-sheet`,
  studentCounts: (id: number) => `${productRoot}/student-projects/${id}/counts`,
  studentCountReport: (id: number) => `${productRoot}/student-projects/${id}/report`,

  bookSurveyProjects: `${productRoot}/book-survey-projects`,
  booklistSheet: (id: number) => `${productRoot}/book-survey-projects/${id}/sheet`,
  booklistAnswers: (id: number) => `${productRoot}/book-survey-projects/${id}/answers`,
  booklistDecision: (answerId: number) =>
    `${productRoot}/book-survey-projects/answers/${answerId}/decision`,
} as const;

const promotionRoot = `${API_VERSION.V1}/promotion`;

const PROMOTION = {
  adjustment: `${promotionRoot}/adjustment`,
  adjustmentMembers: (promoItemId: number) => `${promotionRoot}/adjustment/${promoItemId}/members`,
  adjustmentInstitutes: (promoItemId: number, userId: number) =>
    `${promotionRoot}/adjustment/${promoItemId}/members/${userId}/institutes`,
  itemTypes: `${promotionRoot}/item-types`,
  itemType: (id: number) => `${promotionRoot}/item-types/${id}`,
  items: `${promotionRoot}/items`,
  item: (id: number) => `${promotionRoot}/items/${id}`,
  assignments: `${promotionRoot}/assignments`,
} as const;

const marketPromotionRoot = `${API_VERSION.V1}/market-promotion`;

const MARKET_PROMOTION = {
  adjustment: `${marketPromotionRoot}/adjustment`,
  distributionSummary: `${marketPromotionRoot}/distribution-summary`,
  regionSummary: `${marketPromotionRoot}/region-summary`,
  regionProjects: `${marketPromotionRoot}/region-summary/projects`,
  regionDonations: `${marketPromotionRoot}/region-summary/donations`,
  approvals: `${marketPromotionRoot}/approvals`,
  approvalsExport: `${marketPromotionRoot}/approvals/export`,
  approval: (detailId: number) => `${marketPromotionRoot}/approvals/${detailId}`,
  projects: `${marketPromotionRoot}/projects`,
  project: (id: number) => `${marketPromotionRoot}/projects/${id}`,
  budgets: `${marketPromotionRoot}/budgets`,
  budget: (id: number) => `${marketPromotionRoot}/budgets/${id}`,
  donationBatches: `${marketPromotionRoot}/donation-batches`,
  donationBatchMembers: (batchId: number) =>
    `${marketPromotionRoot}/donation-batches/${batchId}/members`,
  donationBatchMembersExport: (batchId: number) =>
    `${marketPromotionRoot}/donation-batches/${batchId}/members/export`,
  donationCandidates: `${marketPromotionRoot}/donation-candidates`,
  donationCandidateLines: `${marketPromotionRoot}/donation-candidates/lines`,
  donationProjects: `${marketPromotionRoot}/donation-projects`,
  confirmDonationBatch: (batchId: number) =>
    `${marketPromotionRoot}/donation-batches/${batchId}/confirm`,
  disburseDonationBatch: (batchId: number) =>
    `${marketPromotionRoot}/donation-batches/${batchId}/disburse`,
  requisitions: `${marketPromotionRoot}/requisitions`,
  requisitionInstitutes: `${marketPromotionRoot}/requisition-institutes`,
  requisition: (id: number) => `${marketPromotionRoot}/requisitions/${id}`,
  approvalTotals: `${marketPromotionRoot}/approval-totals`,
  cashCollectionExport: `${marketPromotionRoot}/cash-collection/export`,
  misApprovals: `${marketPromotionRoot}/mis-approvals`,
  misApprovalsExport: `${marketPromotionRoot}/mis-approvals/export`,
  misCallers: `${marketPromotionRoot}/mis-approvals/callers`,
  misSourceInstitutes: `${marketPromotionRoot}/mis-approvals/source-institutes`,
  decide: (detailId: number) => `${marketPromotionRoot}/approvals/${detailId}`,
  recommend: (detailId: number) => `${marketPromotionRoot}/approvals/${detailId}/recommend`,
  rejectRecommendation: (detailId: number) =>
    `${marketPromotionRoot}/approvals/${detailId}/recommend/reject`,
  amend: (detailId: number) => `${marketPromotionRoot}/mis-approvals/${detailId}/edit`,
  verifyMis: (detailId: number) => `${marketPromotionRoot}/mis-approvals/${detailId}`,
} as const;

const questionRoot = `${API_VERSION.V1}/question`;

const publishRoot = `${API_VERSION.V1}/publish`;

const PUBLISH = {
  candidates: `${publishRoot}/candidates`,
  batches: `${publishRoot}/batches`,
  batchMembers: (batchId: number) => `${publishRoot}/batches/${batchId}/members`,
  batchMembersExport: (batchId: number) => `${publishRoot}/batches/${batchId}/members/export`,
  confirm: (batchId: number) => `${publishRoot}/batches/${batchId}/confirm`,
  disburse: (batchId: number) => `${publishRoot}/batches/${batchId}/disburse`,
  bill: `${publishRoot}/bill`,
  memberBill: (batchId: number, userId: number) =>
    `${publishRoot}/batches/${batchId}/members/${userId}/bill`,
  batchBills: (batchId: number) => `${publishRoot}/batches/${batchId}/bills`,
} as const;

const QUESTION = {
  downloadMonitor: `${questionRoot}/download-monitor`,
  monitorDetail: `${questionRoot}/monitor-detail`,
  projects: `${questionRoot}/projects`,
  instituteSubjects: (projectInstituteId: number) =>
    `${questionRoot}/project-institutes/${projectInstituteId}/subjects`,
  paper: (instituteSubjectId: number) =>
    `${questionRoot}/institute-subjects/${instituteSubjectId}/paper`,
  classes: `${questionRoot}/classes`,
  classById: (id: number) => `${questionRoot}/classes/${id}`,
  subjects: `${questionRoot}/subjects`,
  subjectById: (id: number) => `${questionRoot}/subjects/${id}`,
  classSubjects: `${questionRoot}/class-subjects`,
  classSubjectById: (id: number) => `${questionRoot}/class-subjects/${id}`,
  projectById: (id: number) => `${questionRoot}/projects/${id}`,
  assignments: (projectId: number) => `${questionRoot}/projects/${projectId}/assignments`,
  myAssignments: `${questionRoot}/my-assignments`,
  myProjects: `${questionRoot}/my-projects`,
  assignmentById: (projectInstituteId: number) =>
    `${questionRoot}/assignments/${projectInstituteId}`,
  assignmentSubjects: (projectInstituteId: number) =>
    `${questionRoot}/assignments/${projectInstituteId}/subjects`,
  papers: (instituteSubjectId: number) =>
    `${questionRoot}/institute-subjects/${instituteSubjectId}/papers`,
  paperFile: (paperId: number) => `${questionRoot}/papers/${paperId}/file`,
  collectors: `${questionRoot}/collectors`,
  memberInstitutes: (userId: number) => `${questionRoot}/members/${userId}/institutes`,
} as const;

const zonalStatementRoot = `${API_VERSION.V1}/reports/zonal-statement`;

const ZONAL_STATEMENT = {
  list: zonalStatementRoot,
  expenseStatement: (userId: number) => `${zonalStatementRoot}/members/${userId}/expense-statement`,
  specimenStatement: (userId: number) =>
    `${zonalStatementRoot}/members/${userId}/specimen-statement`,
} as const;

const leaveApplicationsRoot = `${API_VERSION.V1}/leave-applications`;

const LEAVE_APPLICATION = {
  list: leaveApplicationsRoot,
  summary: `${leaveApplicationsRoot}/summary`,
  countsBySubmitter: `${leaveApplicationsRoot}/submitter-counts`,
  hierarchyFilters: `${leaveApplicationsRoot}/managers`,
  detail: (id: number) => `${leaveApplicationsRoot}/${id}`,
  usageSummary: (id: number) => `${leaveApplicationsRoot}/${id}/usage-summary`,
  approve: (id: number) => `${leaveApplicationsRoot}/${id}/approve`,
  reject: (id: number) => `${leaveApplicationsRoot}/${id}/reject`,
} as const;

const leaveReportsRoot = `${API_VERSION.V1}/leave-reports`;

const LEAVE_REPORT = {
  list: leaveReportsRoot,
  export: `${leaveReportsRoot}/export`,
  dates: (userId: number) => `${leaveReportsRoot}/${userId}/dates`,
} as const;

const feedbacksRoot = `${API_VERSION.V1}/feedbacks`;

const FEEDBACK = {
  types: `${API_VERSION.V1}/settings/feedback-types`,
  list: feedbacksRoot,
  detail: (id: number) => `${feedbacksRoot}/${id}`,
  markRead: (id: number) => `${feedbacksRoot}/${id}/read`,
  complete: (id: number) => `${feedbacksRoot}/${id}/complete`,
} as const;

const instituteTypesRoot = `${API_VERSION.V1}/settings/institute-types`;

const INSTITUTE_TYPE = {
  list: instituteTypesRoot,
  listActive: `${instituteTypesRoot}/active`,
  create: instituteTypesRoot,
  update: (id: number) => `${instituteTypesRoot}/${id}`,
  delete: (id: number) => `${instituteTypesRoot}/${id}`,
} as const;

const examBoardsRoot = `${API_VERSION.V1}/settings/exam-boards`;

const EXAM_BOARD = {
  listActive: examBoardsRoot,
} as const;

const institutesRoot = `${API_VERSION.V1}/institutes`;

const INSTITUTE = {
  list: institutesRoot,
  create: institutesRoot,
  update: (id: number) => `${institutesRoot}/${id}`,
  delete: (id: number) => `${institutesRoot}/${id}`,
  detail: (id: number) => `${institutesRoot}/${id}`,
} as const;

const instituteMappingsRoot = `${API_VERSION.V1}/institute-mappings`;

const INSTITUTE_MAPPING = {
  assigned: `${instituteMappingsRoot}/assigned`,
  unassigned: `${instituteMappingsRoot}/unassigned`,
  assign: `${instituteMappingsRoot}/assign`,
  remove: `${instituteMappingsRoot}/remove`,
} as const;

const instituteSubmissionsRoot = `${API_VERSION.V1}/institute-submissions`;

const INSTITUTE_SUBMISSION = {
  list: instituteSubmissionsRoot,
  summary: `${instituteSubmissionsRoot}/summary`,
  countsBySubmitter: `${instituteSubmissionsRoot}/submitter-counts`,
  detail: (id: number) => `${instituteSubmissionsRoot}/${id}`,
  approve: (id: number) => `${instituteSubmissionsRoot}/${id}/approve`,
  reject: (id: number) => `${instituteSubmissionsRoot}/${id}/reject`,
} as const;

const teachersRoot = `${API_VERSION.V1}/teachers`;

const TEACHER = {
  list: teachersRoot,
  summary: `${teachersRoot}/summary`,
  countsBySubmitter: `${teachersRoot}/submitter-counts`,
  detail: (id: number) => `${teachersRoot}/${id}`,
  activeDesignations: `${API_VERSION.V1}/settings/teacher-designations/active`,
} as const;

const teacherInactivationsRoot = `${API_VERSION.V1}/teacher-inactivation-requests`;

const TEACHER_INACTIVATION = {
  list: teacherInactivationsRoot,
  summary: `${teacherInactivationsRoot}/summary`,
  detail: (id: number) => `${teacherInactivationsRoot}/${id}`,
  approve: (id: number) => `${teacherInactivationsRoot}/${id}/approve`,
  reject: (id: number) => `${teacherInactivationsRoot}/${id}/reject`,
} as const;

const surveysRoot = `${API_VERSION.V1}/surveys`;

const SURVEY = {
  list: surveysRoot,
  detail: (id: number) => `${surveysRoot}/${id}`,
  create: surveysRoot,
  update: (id: number) => `${surveysRoot}/${id}`,
  submissions: (id: number) => `${surveysRoot}/${id}/submissions`,
  submissionDetail: (id: number, submissionId: number) =>
    `${surveysRoot}/${id}/submissions/${submissionId}`,
  submissionsExport: (id: number) => `${surveysRoot}/${id}/submissions/export`,
} as const;

const pcmpProjectsRoot = `${API_VERSION.V1}/pcmp/projects`;

const PCMP_PROJECT = {
  list: pcmpProjectsRoot,
  options: `${pcmpProjectsRoot}/options`,
  detail: (id: number) => `${pcmpProjectsRoot}/${id}`,
  create: pcmpProjectsRoot,
  update: (id: number) => `${pcmpProjectsRoot}/${id}`,
  status: (id: number) => `${pcmpProjectsRoot}/${id}/status`,
  deleteCheck: (id: number) => `${pcmpProjectsRoot}/${id}/delete-check`,
  delete: (id: number) => `${pcmpProjectsRoot}/${id}`,
} as const;

const PCMP_CATEGORY = {
  create: `${API_VERSION.V1}/pcmp/categories`,
} as const;

const pcmpProposalsRoot = `${API_VERSION.V1}/pcmp/proposals`;

const PCMP_PROPOSAL = {
  detail: (id: number) => `${pcmpProposalsRoot}/${id}`,
  editSection: (id: number, section: 'institute' | 'students' | 'teacher' | 'payment') =>
    `${pcmpProposalsRoot}/${id}/${section}`,
  paymentSource: (id: number) => `${pcmpProposalsRoot}/${id}/payment-source`,
  marketingApprove: (id: number) => `${pcmpProposalsRoot}/${id}/marketing/approve`,
  marketingReject: (id: number) => `${pcmpProposalsRoot}/${id}/marketing/reject`,
  marketingExplanation: (id: number) => `${pcmpProposalsRoot}/${id}/marketing/explanation`,
  misDecision: (id: number) => `${pcmpProposalsRoot}/${id}/mis/decision`,
  accountsDisbursementFailed: (id: number) =>
    `${pcmpProposalsRoot}/${id}/accounts/disbursement-failed`,
  accountsReturn: (id: number) => `${pcmpProposalsRoot}/${id}/accounts/return`,
  disbursementConfirm: (id: number) => `${pcmpProposalsRoot}/${id}/disbursement/confirm`,
  correctionPayment: (id: number) => `${pcmpProposalsRoot}/${id}/correction/payment`,
  correctionApprove: (id: number) => `${pcmpProposalsRoot}/${id}/correction/approve`,
  marketingDisbursement: (id: number) => `${pcmpProposalsRoot}/${id}/marketing-disbursement`,
  marketingReturn: (id: number) => `${pcmpProposalsRoot}/${id}/marketing-disbursement/return`,
  hold: (id: number) => `${pcmpProposalsRoot}/${id}/hold`,
  release: (id: number) => `${pcmpProposalsRoot}/${id}/release`,
  adminReject: (id: number) => `${pcmpProposalsRoot}/${id}/admin-reject`,
  requestProcess: (id: number) => `${pcmpProposalsRoot}/${id}/request/process`,
  requestApprove: (id: number) => `${pcmpProposalsRoot}/${id}/request/approve`,
  requestReject: (id: number) => `${pcmpProposalsRoot}/${id}/request/reject`,
} as const;

const pcmpMarketingRoot = `${API_VERSION.V1}/pcmp/marketing`;

const PCMP_MARKETING = {
  queue: `${pcmpMarketingRoot}/queue`,
  queueCounts: `${pcmpMarketingRoot}/queue/counts`,
  queueXlsx: `${pcmpMarketingRoot}/queue/export.xlsx`,
  disbursementsXlsx: `${pcmpMarketingRoot}/disbursements/export.xlsx`,
  proposalCorrection: `${pcmpMarketingRoot}/proposal-correction`,
  proposalCorrectionXlsx: `${pcmpMarketingRoot}/proposal-correction/export.xlsx`,
  batches: `${pcmpMarketingRoot}/batches`,
  batchProposals: (batchNo: number) => `${pcmpMarketingRoot}/batches/${batchNo}/proposals`,
  batchXlsx: (batchNo: number) => `${pcmpMarketingRoot}/batches/${batchNo}/export.xlsx`,
} as const;

const pcmpCorrectionRoot = `${API_VERSION.V1}/pcmp/correction`;

const PCMP_CORRECTION = {
  queue: `${pcmpCorrectionRoot}/queue`,
  queueCounts: `${pcmpCorrectionRoot}/queue/counts`,
} as const;

const pcmpMisRoot = `${API_VERSION.V1}/pcmp/mis`;

const PCMP_MIS = {
  queue: `${pcmpMisRoot}/queue`,
  queueCounts: `${pcmpMisRoot}/queue/counts`,
  queueXlsx: `${pcmpMisRoot}/queue/export.xlsx`,
  batches: `${pcmpMisRoot}/batches`,
  batchProposals: (batchNo: number) => `${pcmpMisRoot}/batches/${batchNo}/proposals`,
  publish: (batchNo: number) => `${pcmpMisRoot}/batches/${batchNo}/publish`,
  rollback: (batchNo: number) => `${pcmpMisRoot}/batches/${batchNo}/rollback`,
  report: (batchNo: number) => `${pcmpMisRoot}/batches/${batchNo}/report`,
  exportXlsx: (batchNo: number) => `${pcmpMisRoot}/batches/${batchNo}/export.xlsx`,
};

const pcmpAccountsRoot = `${API_VERSION.V1}/pcmp/accounts`;

const PCMP_ACCOUNTS = {
  queue: `${pcmpAccountsRoot}/queue`,
  queueCounts: `${pcmpAccountsRoot}/queue/counts`,
  batches: `${pcmpAccountsRoot}/batches`,
  batchProposals: (batchNo: number) => `${pcmpAccountsRoot}/batches/${batchNo}/proposals`,
  disburse: (batchNo: number) => `${pcmpAccountsRoot}/batches/${batchNo}/disburse`,
  disbursementFailed: (batchNo: number) =>
    `${pcmpAccountsRoot}/batches/${batchNo}/disbursement-failed`,
  report: (batchNo: number) => `${pcmpAccountsRoot}/batches/${batchNo}/report`,
  exportXlsx: (batchNo: number) => `${pcmpAccountsRoot}/batches/${batchNo}/export.xlsx`,
  redisburseBatches: `${pcmpAccountsRoot}/redisburse-batches`,
  redisburseBatchProposals: (batchNo: number) =>
    `${pcmpAccountsRoot}/redisburse-batches/${batchNo}/proposals`,
  redisburseDisburse: (batchNo: number) =>
    `${pcmpAccountsRoot}/redisburse-batches/${batchNo}/disburse`,
  redisburseReport: (batchNo: number) => `${pcmpAccountsRoot}/redisburse-batches/${batchNo}/report`,
  redisburseExportXlsx: (batchNo: number) =>
    `${pcmpAccountsRoot}/redisburse-batches/${batchNo}/export.xlsx`,
};

const pcmpDisburseVerifyRoot = `${API_VERSION.V1}/pcmp/disburse-verify`;

const PCMP_DISBURSE_VERIFY = {
  queue: `${pcmpDisburseVerifyRoot}/queue`,
  queueCounts: `${pcmpDisburseVerifyRoot}/queue/counts`,
  verify: `${pcmpDisburseVerifyRoot}/verify`,
} as const;

const pcmpRequestCategoriesRoot = `${API_VERSION.V1}/pcmp/request-categories`;

const PCMP_REQUEST_CATEGORY = {
  list: pcmpRequestCategoriesRoot,
  actionPlans: `${pcmpRequestCategoriesRoot}/action-plans`,
  create: pcmpRequestCategoriesRoot,
  update: (id: number) => `${pcmpRequestCategoriesRoot}/${id}`,
} as const;

const pcmpRequestsRoot = `${API_VERSION.V1}/pcmp/requests`;

const PCMP_REQUEST = {
  categories: `${pcmpRequestsRoot}/categories`,
  queue: `${pcmpRequestsRoot}/queue`,
  queueCounts: `${pcmpRequestsRoot}/queue/counts`,
  batches: `${pcmpRequestsRoot}/batches`,
  batchProposals: (batchNo: number) => `${pcmpRequestsRoot}/batches/${batchNo}/proposals`,
  urgentRequests: `${pcmpRequestsRoot}/urgent-requests`,
  submit: pcmpRequestsRoot,
  requested: `${pcmpRequestsRoot}/requested`,
  requestedCounts: `${pcmpRequestsRoot}/requested/counts`,
  act: `${pcmpRequestsRoot}/act`,
} as const;

const pcmpUrgentRoot = `${API_VERSION.V1}/pcmp/urgent/batches`;

const PCMP_URGENT = {
  stage: pcmpUrgentRoot,
  publish: (batchNo: number) => `${pcmpUrgentRoot}/${batchNo}/publish`,
  rollback: (batchNo: number) => `${pcmpUrgentRoot}/${batchNo}/rollback`,
  report: (batchNo: number) => `${pcmpUrgentRoot}/${batchNo}/report`,
  exportXlsx: (batchNo: number) => `${pcmpUrgentRoot}/${batchNo}/export.xlsx`,
} as const;

const pcmpReportRoot = `${API_VERSION.V1}/pcmp/reports`;

const PCMP_REPORT = {
  filterOptions: `${pcmpReportRoot}/filter-options`,
  pending: `${pcmpReportRoot}/pending`,
  pendingXlsx: `${pcmpReportRoot}/pending/export.xlsx`,
  performance: `${pcmpReportRoot}/performance`,
  performanceXlsx: `${pcmpReportRoot}/performance/export.xlsx`,
  moWise: `${pcmpReportRoot}/mo-wise`,
  moWiseXlsx: `${pcmpReportRoot}/mo-wise/export.xlsx`,
  misStatus: `${pcmpReportRoot}/mis-status`,
  misStatusMembers: `${pcmpReportRoot}/mis-status/members`,
  misStatusXlsx: `${pcmpReportRoot}/mis-status/export.xlsx`,
} as const;

const pcmpLookupRoot = `${API_VERSION.V1}/pcmp/lookups`;

const PCMP_LOOKUP = {
  mos: `${pcmpLookupRoot}/mos`,
  reportingUsers: `${pcmpLookupRoot}/reporting-users`,
  departments: `${pcmpLookupRoot}/departments`,
} as const;

const PCMP_DEPARTMENT = {
  update: (id: number) => `${API_VERSION.V1}/pcmp/departments/${id}`,
} as const;

const pcmpEditPermissionRoot = `${API_VERSION.V1}/pcmp/edit-permissions`;

const PCMP_EDIT_PERMISSION = {
  list: pcmpEditPermissionRoot,
  users: `${pcmpEditPermissionRoot}/users`,
  one: (userId: number) => `${pcmpEditPermissionRoot}/${userId}`,
} as const;

const PCMP_ACCESS = {
  me: `${API_VERSION.V1}/pcmp/me/permissions`,
};

const PCMP_STATUS = {
  catalog: `${API_VERSION.V1}/pcmp/statuses`,
} as const;
const moTravelRoot = `${API_VERSION.V1}/geo-location/mo-travel`;

const MO_TRAVEL = {
  list: moTravelRoot,
  summary: `${moTravelRoot}/summary`,
  route: (userId: number) => `${moTravelRoot}/${userId}`,
} as const;

const rbacRoot = `${API_VERSION.V1}/rbac`;
const rolesRoot = `${rbacRoot}/roles`;

const ROLE = {
  list: rolesRoot,
  create: rolesRoot,
  update: (id: number) => `${rolesRoot}/${id}`,
  delete: (id: number) => `${rolesRoot}/${id}`,
  permissions: (id: number) => `${rolesRoot}/${id}/permissions`,
  setPermissions: (id: number) => `${rolesRoot}/${id}/permissions`,
} as const;

const PERMISSION = {
  catalogue: `${rbacRoot}/permissions`,
  userRoles: (userId: number) => `${rbacRoot}/users/${userId}/roles`,
  setUserRoles: (userId: number) => `${rbacRoot}/users/${userId}/roles`,
  userPermissions: (userId: number) => `${rbacRoot}/users/${userId}/permissions`,
  userDirectPermissions: (userId: number) => `${rbacRoot}/users/${userId}/direct-permissions`,
  setUserPermissions: (userId: number) => `${rbacRoot}/users/${userId}/direct-permissions`,
} as const;

const UPLOADS = {
  presign: `${API_VERSION.V1}/uploads/presign`,
} as const;

export const API = {
  auth: AUTH,
  users: USERS,
  attendance: ATTENDANCE,
  organization: ORGANIZATION,
  geo: GEO,
  settings: SETTINGS,
  leaveType: LEAVE_TYPE,
  holiday: HOLIDAY,
  limitType: LIMIT_TYPE,
  webUrl: WEB_URL,
  appVersion: APP_VERSION,
  appVersionCheck: APP_VERSION_CHECK,
  dashboard: DASHBOARD,
  dcrSummary: DCR_SUMMARY,
  zonalStatement: ZONAL_STATEMENT,
  billing: BILLING,
  settingsLookups: SETTINGS_LOOKUPS,
  product: PRODUCT,
  promotion: PROMOTION,
  marketPromotion: MARKET_PROMOTION,
  publish: PUBLISH,
  question: QUESTION,
  leaveApplication: LEAVE_APPLICATION,
  leaveReport: LEAVE_REPORT,
  feedback: FEEDBACK,
  instituteType: INSTITUTE_TYPE,
  institute: INSTITUTE,
  examBoard: EXAM_BOARD,
  moTravel: MO_TRAVEL,
  role: ROLE,
  permission: PERMISSION,
  instituteMapping: INSTITUTE_MAPPING,
  instituteSubmission: INSTITUTE_SUBMISSION,
  teacher: TEACHER,
  teacherInactivation: TEACHER_INACTIVATION,
  survey: SURVEY,
  pcmpProject: PCMP_PROJECT,
  pcmpCategory: PCMP_CATEGORY,
  pcmpProposal: PCMP_PROPOSAL,
  pcmpMarketing: PCMP_MARKETING,
  pcmpCorrection: PCMP_CORRECTION,
  pcmpMis: PCMP_MIS,
  pcmpAccounts: PCMP_ACCOUNTS,
  pcmpDisburseVerify: PCMP_DISBURSE_VERIFY,
  pcmpRequestCategory: PCMP_REQUEST_CATEGORY,
  pcmpRequest: PCMP_REQUEST,
  pcmpUrgent: PCMP_URGENT,
  pcmpReport: PCMP_REPORT,
  pcmpLookup: PCMP_LOOKUP,
  pcmpDepartment: PCMP_DEPARTMENT,
  pcmpEditPermission: PCMP_EDIT_PERMISSION,
  pcmpAccess: PCMP_ACCESS,
  pcmpStatus: PCMP_STATUS,
  uploads: UPLOADS,
} satisfies Record<string, Record<string, unknown>>;
